import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from 'assets/images/close-icon.svg';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { useScheduleEventSelection } from '../hooks/useScheduleEventSelection';
import { useEventHeight } from '../hooks/useEventHeight';
import classnames from 'classnames';
import { getSheetEventTitle } from '../../utils';
import useScheduleEventCardData from '../hooks/useScheduleEventCardData';
import { hexToRgba } from 'helpers/hexToRgba';
import { useHandleDrop } from './hooks/useHandleResourceDrop';
import ArrowUp from 'assets/images/arrow-up-icon.svg';
import CancelIcon from 'assets/images/cancel-icon.svg';


export const ScheduleEventCardV6 = ({
  event,
  locationColor,
  handleClick,
  handleDelete,
  dateFormat,
  timeFormat,
  noClSheets,
  hasEditAccess,
}) => {
  const { sheet } = event.extendedProps || {};
  const { handleDrop } = useHandleDrop({ sheet })

  const { originalTimeHeight, shouldRenderPrevDayBlock, shouldRenderNextDayBlock } = useEventHeight(event);

  const { selectedWorkers, selectedEquipment, onWorkerClick, onEquipmentClick } = useScheduleEventSelection(sheet?._id);

  const title = getSheetEventTitle(sheet, 'short', 'jobNumber'); // sheet.submittedAt && hiddenResources
  const { jobNumber, contractor, statusText, status, timeRange, workers, equipment } = useScheduleEventCardData(event);

  const shouldRenderDeleteButton = useMemo(() => (
    !locationColor &&
    !sheet.submittedAt &&
    !sheet.published &&
    hasEditAccess
  ), [sheet, locationColor, hasEditAccess]);

  const setDragElementData = (resourceId, resourceType) => (e) => {
    if (locationColor) return;
    e.dataTransfer.setData('resourceId', resourceId);
    e.dataTransfer.setData('resourceType', resourceType);
    e.dataTransfer.setData('sheetsId', sheet._id);
  };

  return (
    <div
      className="schedule-v6-event-card"
      onClick={handleClick}
      data-original-time-height={originalTimeHeight}
      onDragOver={(e) => e.preventDefault()}
      onDrop={sheet.canceledAt ? null : handleDrop}
    >
      <div className={classnames(
        "schedule-v6-event-card-content",
        { 'publish-error': noClSheets.includes(sheet._id) }
      )}>
        <div className={classnames("schedule-v6-event-general", sheet.timeOfDay)}>
          {shouldRenderPrevDayBlock && (
            <div className="schedule-v6-event-day-indicator schedule-v6-event-day-indicator--prev">
              <img src={ArrowUp} alt="arrow" className="schedule-v6-event-day-indicator--arrow" />
              Previous day
              <img src={ArrowUp} alt="arrow" className="schedule-v6-event-day-indicator--arrow" />
            </div>
          )}
          {event.extendedProps?.locationColor ? (
            <div
              className="schedule-v6-event-location-mark"
              style={{
                borderTopColor: event.extendedProps?.locationColor,
                borderRightColor: event.extendedProps?.locationColor,
              }}
            />
          ) : null}
          <div className={classnames("schedule-v6-event-status", status)}>
            <Typography className="schedule-v6-event-status-text">{statusText}</Typography>
          </div>
          {shouldRenderDeleteButton ? (
            <Button
              color="secondary"
              className={classnames(
                "schedule-v6-event-delete-button",
                shouldRenderPrevDayBlock && "fix-top"
              )}
              onClick={handleDelete}
              disableRipple
            >
              <img src={CloseIcon} alt="delete" />
            </Button>
          ) : null}
          <div className="schedule-v6-event-title">
            {title}
            <span className="schedule-v6-event-title-job-number">#{jobNumber}</span>
          </div>
          {contractor && (
            <Typography
              variant="body1"
              color="secondary"
              className="schedule-v6-event-meta schedule-v6-event-contractor"
            >
                {contractor}
            </Typography>
          )}
          {timeRange && (
            <Typography
              variant="body1"
              className="schedule-v6-event-meta schedule-v6-event-time"
            >
              {timeRange}
            </Typography>
          )}
        </div>
        {sheet.canceledAt ? (
          <div className="schedule-v6-canceled-container">
            <div className="schedule-v6-canceled-overlay">
              <div className="schedule-v6-canceled-content">
                <Typography variant="body1" className="schedule-v6-canceled-header">
                  <img src={CancelIcon} alt="canceled" />
                  Canceled
                </Typography>
                {sheet.cancelNote && (
                  <Typography variant="body1" className="schedule-v6-canceled-note">
                    {sheet.cancelNote}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        ) : null}
        {!sheet.canceledAt && (workers.length > 0 || equipment.length > 0 || sheet.notes?.length > 0) && (
          <div className="schedule-v6-event-resource-block">
            {!sheet.canceledAt && Boolean(sheet.notes) && (
              <div className="schedule-v6-event-note">
                <Typography variant="body1" className="schedule-v6-event-note-label">Note:</Typography>
                <Typography variant="body1" className="schedule-v6-event-note-text">
                  {sheet.notes.length > 50 ? sheet.notes.substr(0, 50) + '...' : sheet.notes}
                </Typography>
              </div>
            )}
            {workers.length > 0 && (
              <div className="schedule-v6-event-chips-column">
                <div className="schedule-v6-event-resource-header">People: </div>
                {workers.map((worker) => {
                  const isSelected = selectedWorkers.includes(worker._id);
                  return (
                    <div
                      key={worker._id}
                      className={classnames(
                        'schedule-v6-chip', 'schedule-v6-chip--worker',
                        (isSelected ? ' schedule-v6-chip--worker--selected' : ''),
                        worker.shifts?.timeOfDay?.toLowerCase(),
                      )}
                      onClick={onWorkerClick(worker._id)}
                      draggable={!!hasEditAccess}
                      onDragStart={setDragElementData(worker._id, 'workers')}
                      data-type="worker"
                    >
                      {worker.username}
                    </div>
                  );
                })}
              </div>
            )}
            {equipment.length > 0 && (
              <div className="schedule-v6-event-chips-column">
                <div className="schedule-v6-event-resource-header">Equipment: </div>
                {equipment.map((item) => {
                  const isSelected = selectedEquipment.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      className={classnames(
                        'schedule-v6-chip', 'schedule-v6-chip--equipment',
                        (isSelected ? 'schedule-v6-chip--equipment--selected' : ''),
                        item.className,
                      )}
                      onClick={onEquipmentClick(item.id)}
                      style={isSelected ? {
                        backgroundColor: hexToRgba(item.color, 0.1),
                      } : {}}
                      data-type="equipment"
                      draggable={!!hasEditAccess}
                      onDragStart={setDragElementData(item.id, 'equipment')}
                    >
                      <div
                        className="schedule-v6-chip--equipment--number"
                        style={{ backgroundColor: item.color || undefined }}
                      >
                        {item.number || ''}
                      </div>
                      <div
                        className="schedule-v6-chip--equipment--name"
                        style={{ color: item.color || undefined }}
                      >
                        {item.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {shouldRenderNextDayBlock && (
          <div className="schedule-v6-event-day-indicator schedule-v6-event-day-indicator--next">
            <img src={ArrowUp} alt="arrow" className="schedule-v6-event-day-indicator--arrow" />
            Following day
            <img src={ArrowUp} alt="arrow" className="schedule-v6-event-day-indicator--arrow" />
          </div>
        )}
      </div>
    </div>
  );
};

ScheduleEventCardV6.propTypes = {
  event: PropTypes.object.isRequired, // FullCalendar EventApi (with extendedProps.entry)
  locationColor: PropTypes.string,
  handleClick: PropTypes.func,
  handleDelete: PropTypes.func,
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  noClSheets: PropTypes.arrayOf(PropTypes.string),
  hasEditAccess: PropTypes.bool,
};

export default ScheduleEventCardV6;

